import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtGHS } from "@/lib/format";
import { Download, Database, Loader2, FileText } from "lucide-react";

const DS_META = {
  sales: { label: "Sales (line items)", dated: true },
  inventory: { label: "Inventory (on-hand batches)", dated: false },
  transfers: { label: "Stock transfers", dated: true },
  deliveries: { label: "Deliveries", dated: true },
  warehouse_sales: { label: "Warehouse sales by shift & payment type", dated: true },
  inpatient_active: { label: "Inpatients — active admissions", dated: false },
  inpatient_charges: { label: "Inpatients — cumulative charges per patient", dated: true },
  inpatient_settlements: { label: "Inpatients — discharge settlements", dated: true },
  inpatient_dispensing: { label: "Inpatients — dispensing log per staff member", dated: true },
};

const isoDay = (d) => d.toISOString().slice(0, 10);

export default function Reports() {
  const { can, user } = useAuth();
  const { branches } = useBranch();

  const branchLocked = can("reports.branch") && !can("reports.all");
  const branchOptions = branchLocked ? (user?.branches || []) : branches;

  const IP = ["inpatient_active", "inpatient_charges", "inpatient_settlements", "inpatient_dispensing"];
  const base = (can("reports.all") || can("reports.branch"))
    ? Object.keys(DS_META).filter((k) => !IP.includes(k))
    : can("reports.delivery") ? ["deliveries"] : [];
  const available = [...base, ...(can("inpatients.reports") ? IP : [])];

  const [dataset, setDataset] = useState(available[0] || "sales");
  const [branch, setBranch] = useState(branchLocked ? (branchOptions[0]?.id || "") : "all");
  const [to, setTo] = useState(isoDay(new Date()));
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 29 * 86400000)));
  const [ctype, setCtype] = useState("all");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const meta = DS_META[dataset] || {};
  const params = useCallback(() => {
    const p = { limit: 200 };
    if (branch && branch !== "all") p.branch_id = branch;
    if (meta.dated) { p.date_from = from; p.date_to = to; }
    if (dataset === "sales" && ctype !== "all") p.customer_type = ctype;
    return p;
  }, [branch, from, to, meta.dated, dataset, ctype]);

  const run = useCallback(() => {
    if (!dataset) return;
    setLoading(true);
    api.get(`/reports/${dataset}/preview`, { params: params() })
      .then((r) => setResult(r.data))
      .catch((e) => { setResult(null); toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  }, [dataset, params]);

  // Auto-preview when the dataset changes
  useEffect(() => { if (dataset) run(); }, [dataset, ctype]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (format = "xlsx") => {
    setDownloading(format);
    try {
      const res = await api.get(`/reports/${dataset}/download`, { params: { ...params(), format }, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `mobimed_${dataset}.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(format === "csv" ? "CSV file downloaded" : "Excel file downloaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (!available.length) {
    return <div className="p-10 text-center text-muted-foreground">You don't have report access.</div>;
  }

  return (
    <div>
      <PageHeader
        title="Reports & Data Explorer"
        subtitle="Query raw, row-level records and export to Excel for external analysis."
      />

      <Card className="p-4 mb-6 border-border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Dataset</Label>
            <Select value={dataset} onValueChange={setDataset}>
              <SelectTrigger data-testid="reports-dataset-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {available.map((k) => <SelectItem key={k} value={k}>{DS_META[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Branch</Label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger data-testid="reports-branch-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {!branchLocked && <SelectItem value="all">All locations</SelectItem>}
                {branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" data-testid="reports-from" value={from} disabled={!meta.dated}
              onChange={(e) => setFrom(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" data-testid="reports-to" value={to} disabled={!meta.dated}
              onChange={(e) => setTo(e.target.value)} className="mt-1" />
          </div>
        </div>

        {dataset === "sales" && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="reports-ctype-filter">
            <Label className="text-xs mr-1">Sale type</Label>
            {[["all", "All"], ["cash", "Cash"], ["insurance", "Insurance"], ["corporate", "Corporate"]].map(([v, l]) => (
              <Button key={v} size="sm" variant={ctype === v ? "default" : "outline"} className="h-8 rounded-full" onClick={() => setCtype(v)} data-testid={`reports-ctype-${v}`}>{l}</Button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <Button data-testid="reports-run-btn" onClick={run} disabled={loading} variant="secondary">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button data-testid="reports-download-btn" onClick={() => download("xlsx")} disabled={!!downloading || !result}>
            {downloading === "xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download Excel
          </Button>
          <Button data-testid="reports-download-csv-btn" variant="outline" onClick={() => download("csv")} disabled={!!downloading || !result}>
            {downloading === "csv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Download CSV
          </Button>
          {!meta.dated && <span className="text-xs text-muted-foreground">Date range doesn't apply to inventory (current snapshot).</span>}
        </div>
      </Card>

      {dataset === "sales" && result?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="reports-sales-summary">
          {[["Invoices", result.summary.invoices, true], ["Sales value", result.summary.sales], ["Insurance pays", result.summary.insurance],
            ["Client paid", result.summary.client], ["of which co-pay", result.summary.copay], ["On account", result.summary.on_account]].map(([k, v, n]) => (
            <Card key={k} className="p-3 border-border"><div className="text-[11px] text-muted-foreground">{k}</div>
              <div className="font-mono font-semibold" data-testid={`reports-sum-${k.replace(/\s+/g, "-").toLowerCase()}`}>{n ? v : fmtGHS(v)}</div></Card>
          ))}
        </div>
      )}

      <Card className="border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">{DS_META[dataset]?.label}</span>
          {result && (
            <span data-testid="reports-total" className="text-xs text-muted-foreground">
              {result.total} rows{result.total > result.rows.length ? ` · previewing first ${result.rows.length}` : ""}
            </span>
          )}
        </div>
        <div className="overflow-x-auto max-h-[60vh]">
          <table data-testid="reports-preview-table" className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                {(result?.headers || []).map((h) => (
                  <th key={h} className="text-left font-semibold text-muted-foreground px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result && result.rows.length === 0 && (
                <tr><td className="px-3 py-6 text-center text-muted-foreground" colSpan={result.headers.length || 1}>No records for this selection.</td></tr>
              )}
              {(result?.rows || []).map((row, ri) => (
                <tr key={ri} className="border-t border-border hover:bg-muted/40">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 whitespace-nowrap text-foreground">{cell == null ? "—" : String(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
